import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, Copy, Download, Crown, ArrowRight, Camera, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import DiscordLinkButton from '@/components/DiscordLinkButton';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

export default function PaymentSuccess() {
  const loc = useLocation();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const sessionId = new URLSearchParams(loc.search).get('session_id');
  const [status, setStatus] = useState('polling');
  const [order, setOrder] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!sessionId) { nav('/'); return; }
    const provider = new URLSearchParams(loc.search).get('provider');
    let cancelled = false;
    let n = 0;

    async function captureIfPaypal() {
      if (provider !== 'paypal') return;
      try {
        await api.post(`/paypal/capture/${sessionId}`);
      } catch (e) {
        console.warn('PayPal capture request failed', e);
      }
    }

    async function tick() {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setOrder(data);
        if (data.payment_status === 'paid') {
          setStatus('paid');
          refresh();
          return;
        }
        if (data.payment_status === 'failed' || data.payment_status === 'expired') {
          setStatus('failed');
          return;
        }
      } catch (e) { console.warn('payment status poll error', e); }
      n += 1; setAttempts(n);
      if (n < 15) setTimeout(tick, 2000);
      else setStatus('timeout');
    }

    (async () => {
      await captureIfPaypal();
      tick();
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line
  }, [sessionId]);

  const copyCode = async () => {
    if (order?.vip_code) {
      await navigator.clipboard.writeText(order.vip_code);
      setCopied(true); toast.success('VIP code copied');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="bg-grain relative min-h-screen text-white">
      <Navbar />
      <div className="orb" style={{ top: '10%', left: '50%', transform: 'translateX(-50%)', width: 500, height: 500, background: 'radial-gradient(circle, rgb(var(--brand-rgb)/0.5), transparent 70%)' }} />

      <div className="max-w-2xl mx-auto pt-32 pb-20 px-6 relative">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
          <div className="glass-strong rounded-2xl p-10 text-center" data-testid="payment-success-card">
            {status === 'polling' && (
              <>
                <div className="mx-auto h-14 w-14 rounded-full border-4 border-white/10 border-t-brand animate-spin" />
                <h2 className="font-display font-bold text-3xl mt-6 tracking-tight">Confirming payment…</h2>
                <p className="text-white/50 mt-2 text-sm">Attempt {attempts + 1}/15 · usually takes a few seconds.</p>
              </>
            )}
            {status === 'paid' && order?.kind === 'vip' && (
              <>
                <div className="mx-auto h-16 w-16 rounded-full bg-brand/20 flex items-center justify-center text-brand">
                  <Crown className="h-9 w-9" />
                </div>
                <p className="mt-6 text-xs uppercase tracking-[0.35em] text-brand font-mono">Lucky VIP · Lifetime</p>
                <h2 className="font-display font-black text-white text-5xl md:text-6xl mt-3 tracking-tighter leading-none" data-testid="payment-success-title">
                  Purchase Confirmed
                </h2>
                <p className="text-white/60 mt-4">Welcome to Lucky VIP. Every current script + every future release is now unlocked for you.</p>

                {order?.vip_code && (
                  <div className="mt-10 p-8 rounded-3xl border-2 border-brand/40 bg-gradient-to-b from-brand/10 to-brand/5 relative overflow-hidden" data-testid="vip-code-box">
                    <div
                      className="orb"
                      style={{ top: '-50%', left: '50%', transform: 'translateX(-50%)', width: 300, height: 300,
                               background: 'radial-gradient(circle, rgb(var(--brand-rgb)/0.35), transparent 70%)' }}
                    />
                    <div className="relative">
                      <div className="text-[10px] uppercase tracking-[0.4em] text-brand mb-3 font-mono">Your Lucky VIP Code</div>
                      <div className="flex items-center justify-center gap-4 flex-wrap">
                        <span className="font-mono font-black text-4xl md:text-5xl text-white tracking-widest" data-testid="success-vip-code">
                          {order.vip_code}
                        </span>
                        <button
                          onClick={copyCode}
                          data-testid="copy-vip-code-btn"
                          className="h-10 w-10 rounded-full bg-white/10 border border-white/10 hover:bg-white/20 text-white/80 flex items-center justify-center transition-colors"
                        >
                          {copied ? <Check className="h-5 w-5 text-brand" /> : <Copy className="h-5 w-5" />}
                        </button>
                      </div>
                      <p className="text-xs text-white/50 mt-4 max-w-xs mx-auto leading-relaxed">
                        This code grants lifetime access to every VIP release. You can find it anytime inside your dashboard.
                      </p>
                    </div>
                  </div>
                )}

                <div className="mt-6 flex items-center justify-center gap-3 rounded-2xl border border-amber-400/30 bg-amber-400/[0.05] px-5 py-4" data-testid="screenshot-prompt">
                  <Camera className="h-5 w-5 text-amber-300 shrink-0" />
                  <p className="text-sm text-amber-100/90 text-left leading-relaxed">
                    <span className="font-semibold">Screenshot this page &amp; your VIP code.</span> Save it somewhere safe — it&apos;s your lifetime access key.
                  </p>
                </div>

                <div className="mt-8 grid grid-cols-3 gap-3 text-left">
                  <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
                    <ShieldCheck className="h-4 w-4 text-brand" />
                    <div className="text-xs uppercase tracking-widest text-white/40 mt-2 font-mono">Order</div>
                    <div className="text-sm text-white font-mono mt-1 truncate">{sessionId?.slice(-12)}</div>
                  </div>
                  <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
                    <Crown className="h-4 w-4 text-brand" />
                    <div className="text-xs uppercase tracking-widest text-white/40 mt-2 font-mono">Status</div>
                    <div className="text-sm text-brand font-semibold mt-1">Lifetime</div>
                  </div>
                  <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
                    <Download className="h-4 w-4 text-brand" />
                    <div className="text-xs uppercase tracking-widest text-white/40 mt-2 font-mono">Unlocked</div>
                    <div className="text-sm text-white font-semibold mt-1">VIP Perks</div>
                  </div>
                </div>

                {/* Discord link CTA */}
                <DiscordLinkButton />

                <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
                  <Link to="/dashboard" data-testid="go-dashboard-btn"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 text-white font-medium transition-colors">
                    <Crown className="h-4 w-4" /> Open VIP dashboard <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </>
            )}
            {status === 'paid' && order?.kind !== 'vip' && (
              <>
                <div className="mx-auto h-14 w-14 rounded-full bg-brand/20 flex items-center justify-center text-brand"><Check className="h-8 w-8" /></div>
                <h2 className="font-display font-bold text-4xl mt-6 tracking-tight" data-testid="payment-success-title">Payment received</h2>
                <p className="text-white/60 mt-3">Order <span className="font-mono text-white">{sessionId?.slice(-12)}</span> is confirmed.</p>
                <p className="text-white/40 text-xs mt-2">A receipt is available inside your dashboard.</p>

                <div className="mt-8 rounded-2xl border border-white/10 bg-white/[0.03] p-5 text-left">
                  <div className="font-display font-semibold mb-2">Installation overview</div>
                  <p className="text-white/40 text-xs mb-3">Full guide inside the download package.</p>
                  <ol className="text-sm text-white/60 space-y-1 list-decimal list-inside">
                    <li>Head to your dashboard.</li>
                    <li>Click &quot;Download&quot; on your unlocked product.</li>
                    <li>Import the profile into XIM Manager / Zen Studio.</li>
                    <li>Follow the included README for hotkey bindings.</li>
                  </ol>
                </div>

                <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
                  <Link to="/dashboard" data-testid="go-dashboard-btn"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand text-[#0a0a0a] font-semibold hover:shadow-[0_0_30px_rgb(var(--brand-rgb)/0.6)] transition-shadow">
                    <Download className="h-4 w-4" /> Go to dashboard <ArrowRight className="h-4 w-4" />
                  </Link>
                  <Link to="/" className="text-sm text-white/50 hover:text-white transition-colors">Back to store</Link>
                </div>
              </>
            )}
            {status === 'failed' && (
              <>
                <h2 className="font-display font-bold text-3xl mt-4">Payment failed</h2>
                <p className="text-white/50 mt-2">Your payment could not be completed. Please try again.</p>
                <Link to="/" className="mt-6 inline-block text-brand hover:underline">Back to store →</Link>
              </>
            )}
            {status === 'timeout' && (
              <>
                <h2 className="font-display font-bold text-3xl mt-4">Still processing…</h2>
                <p className="text-white/50 mt-2">This is taking longer than usual. Refresh your dashboard in a minute.</p>
                <Link to="/dashboard" className="mt-6 inline-block text-brand hover:underline">Open dashboard →</Link>
              </>
            )}
          </div>
        </motion.div>
      </div>
      <Footer />
    </div>
  );
}
